import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TagIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  Squares2X2Icon,
  TableCellsIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { catalogApi } from '../../services/api';
import type { Service, ServiceCategory } from '../../types';
import ServiceItemManagement from '../../components/ServiceItemManagement';

const CatalogList: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Valor do input (atualiza imediatamente)
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [isActiveFilter, setIsActiveFilter] = useState<string>('true'); // Filtro ativo/inativo (padrão: apenas ativos)
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [pageSize, setPageSize] = useState(15);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    current_page: 1
  });

  // Modal states
  const [selectedServiceForItems, setSelectedServiceForItems] = useState<Service | null>(null);
  const [showItemManagement, setShowItemManagement] = useState(false);
  const [initialFilter, setInitialFilter] = useState<string>('all');

  // Debounce para busca - só busca com 3+ caracteres ou se estiver vazio
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const params: any = {
          search: searchTerm || undefined,
          category_slug: selectedCategory || undefined,
          availability_status: selectedAvailability || undefined,
          featured: showFeaturedOnly || undefined,
          is_active: isActiveFilter === 'all' ? undefined : (isActiveFilter === 'true'),
          page_size: pageSize,
          ordering: 'category__name,name',
        };

        const [servicesResponse, categoriesResponse] = await Promise.all([
          catalogApi.getServices(params),
          catalogApi.getCategories({ is_active: true, ordering: 'name' })
        ]);

        setServices(servicesResponse.results);
        setCategories(categoriesResponse);
        setPagination({
          count: servicesResponse.count || 0,
          next: servicesResponse.next || null,
          previous: servicesResponse.previous || null,
          current_page: 1
        });
        setError(null);
      } catch (err) {
        setError('Erro ao carregar equipamentos');
        console.error('Error loading catalog:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [searchTerm, selectedCategory, selectedAvailability, showFeaturedOnly, isActiveFilter, pageSize]);

  const getAvailabilityIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'unavailable':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'maintenance':
        return <WrenchScrewdriverIcon className="h-4 w-4 text-yellow-500" />;
      case 'rented':
        return <ExclamationCircleIcon className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getAvailabilityColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'unavailable':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'rented':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (price: number | string | undefined) => {
    if (!price) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numPrice);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setSelectedCategory('');
    setSelectedAvailability('');
    setShowFeaturedOnly(false);
    setIsActiveFilter('true'); // Volta ao padrão: apenas ativos
  };

  const handleOpenItemManagement = (service: Service, filterBy: string = 'all') => {
    setSelectedServiceForItems(service);
    setInitialFilter(filterBy);
    setShowItemManagement(true);
  };

  const handleCloseItemManagement = () => {
    setShowItemManagement(false);
    setSelectedServiceForItems(null);
    setInitialFilter('all');
  };

  const handleDuplicate = async (serviceId: string) => {
    try {
      // Fetch full service details
      const service = await catalogApi.getService(serviceId);

      // Store the data in sessionStorage for the form to pick up
      const duplicateData = {
        ...service,
        id: undefined, // Clear ID to create new
        code: undefined, // User must provide new code
        name: `${service.name} (Cópia)`,
        // Clear placa and alias so user must enter new ones
        technical_specs: {
          ...service.technical_specs,
          placa: '',
          alias: service.technical_specs?.alias ? `${service.technical_specs.alias} - Cópia` : ''
        }
      };

      sessionStorage.setItem('duplicateServiceData', JSON.stringify(duplicateData));
      navigate('/catalog/services/new');
    } catch (err) {
      console.error('Error duplicating service:', err);
      setError('Erro ao duplicar veículo');
    }
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {services.map((service) => (
        <div key={service.id} className="bg-white overflow-hidden shadow rounded-lg">
          {/* Service Image */}
          <div className="relative">
            {service.primary_image?.image_url ? (
              <img
                className="h-48 w-full object-cover"
                src={service.primary_image.image_url}
                alt={service.primary_image.title || service.name}
              />
            ) : (
              <div className="h-48 w-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-sm">Sem imagem</span>
              </div>
            )}
            
            {/* Featured badge */}
            {service.featured && (
              <div className="absolute top-2 right-2">
                <StarSolidIcon className="h-6 w-6 text-yellow-500" />
              </div>
            )}

            {/* Availability status */}
            <div className="absolute bottom-2 left-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(service.availability_status)}`}>
                {getAvailabilityIcon(service.availability_status)}
                <span className="ml-1">{service.availability_label}</span>
              </span>
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {service.category_is_vehicle && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                      🚗 VEÍCULO
                    </span>
                  )}
                  {service.code && (
                    <span className="text-sm text-blue-600 font-mono font-medium">{service.code}</span>
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {service.category_is_vehicle && service.technical_specs?.placa ? (
                    <>
                      <span className="font-bold text-blue-900">{service.technical_specs.placa}</span>
                      {service.technical_specs.alias && (
                        <div className="text-base text-gray-600">{service.technical_specs.alias}</div>
                      )}
                    </>
                  ) : (
                    service.name
                  )}
                </h3>
                <p className="text-xs text-gray-500">{service.category_name}</p>
              </div>
            </div>

            {service.short_description && (
              <p className="mt-0.5 text-xs text-gray-600 line-clamp-2">
                {service.short_description}
              </p>
            )}

            {/* Inventory Status */}
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Visão Geral:</span>

                {/* SERVIÇOS: Apenas ícone clicável */}
                {service.item_type === 'service' ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: Abrir modal de contratos/clientes para serviços
                      alert('Modal de contratos ativos em desenvolvimento');
                    }}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    title="Ver contratos e clientes"
                  >
                    <InformationCircleIcon className="h-4 w-4" />
                  </button>
                ) : (
                  /* PRODUTOS: Mostrar disponível/total + ícone para modal */
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium">
                      <span className="text-green-600">{service.available_quantity || 0}</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-gray-900">{service.total_quantity || 0}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenItemManagement(service, 'all');
                      }}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="Gerenciar itens individuais"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Barra de progresso apenas para produtos */}
              {service.item_type !== 'service' && service.track_individual_items && (
                <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-green-600 h-1.5 rounded-full"
                    style={{
                      width: `${((service.available_quantity || 0) / (service.total_quantity || 1)) * 100}%`
                    }}
                  ></div>
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="mt-4 space-y-1">
              {service.daily_price && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Diária:</span>
                  <span className="font-medium">{formatPrice(service.daily_price)}</span>
                </div>
              )}
              {service.weekly_price && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Semanal:</span>
                  <span className="font-medium">{formatPrice(service.weekly_price)}</span>
                </div>
              )}
              {service.monthly_price && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Mensal:</span>
                  <span className="font-medium">{formatPrice(service.monthly_price)}</span>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-between items-center">
              <Link
                to={`/catalog/services/${service.id}`}
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                Ver detalhes
              </Link>
              <div className="flex items-center space-x-2">
                {service.category_is_vehicle && (
                  <button
                    onClick={() => handleDuplicate(service.id)}
                    className="text-green-600 hover:text-green-700 text-sm flex items-center"
                    title="Duplicar veículo"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                    Duplicar
                  </button>
                )}
                <Link
                  to={`/catalog/services/${service.id}/edit`}
                  className="text-gray-600 hover:text-gray-500 text-sm"
                >
                  Editar
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-bold text-gray-900">Catálogo de Equipamentos</h1>
            <p className="mt-0.5 text-xs text-gray-700">
              Gerencie o catálogo de equipamentos disponíveis para locação.
            </p>
          </div>
          <div className="mt-2 sm:mt-0 sm:ml-16 sm:flex-none">
            <div className="flex items-center space-x-2">
              {/* Toggle de visualização */}
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                    viewMode === 'table' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  title="Vista de tabela (mais compacta)"
                >
                  <TableCellsIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  title="Vista de cards (mais visual)"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
              </div>
              
              <Link
                to="/catalog/categories"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <TagIcon className="h-4 w-4 mr-1.5" />
                Categorias
              </Link>
              <Link
                to="/catalog/services/new"
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Novo Equipamento
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {/* Search */}
          <div className="lg:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Buscar por nome, código, placa ou alias (mín. 3 caracteres)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <select
              id="category"
              name="category"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Todas</option>
              {categories.map((category) => (
                <option key={category.id} value={category.slug}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Availability */}
          <div>
            <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
              Disponibilidade
            </label>
            <select
              id="availability"
              name="availability"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={selectedAvailability}
              onChange={(e) => setSelectedAvailability(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="available">Disponível</option>
              <option value="unavailable">Indisponível</option>
              <option value="maintenance">Em Manutenção</option>
              <option value="rented">Alugado</option>
            </select>
          </div>

          {/* Status Ativo/Inativo */}
          <div>
            <label htmlFor="isActive" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="isActive"
              name="isActive"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={isActiveFilter}
              onChange={(e) => setIsActiveFilter(e.target.value)}
            >
              <option value="true">Apenas Ativos</option>
              <option value="false">Apenas Inativos</option>
              <option value="all">Todos</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          {/* Featured toggle */}
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
              checked={showFeaturedOnly}
              onChange={(e) => setShowFeaturedOnly(e.target.checked)}
            />
            <span className="ml-2 text-sm text-gray-700">Apenas em destaque</span>
          </label>

          {/* Clear filters */}
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-4 w-4 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Services List - Visualização Dinâmica */}
      {services.length > 0 ? (
        viewMode === 'table' ? (
        <>
          {/* Responsive Cards for Mobile */}
          <div className="block lg:hidden space-y-3 mb-6">
          {services.map((service) => (
            <div key={service.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center flex-1">
                  {service.primary_image?.image_url ? (
                    <img className="h-12 w-12 rounded-lg object-cover mr-3" src={service.primary_image.image_url} alt={service.name} />
                  ) : (
                    <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-gray-400 text-xs">IMG</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-1 flex-wrap">
                      {service.category_is_vehicle && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                          🚗 VEÍCULO
                        </span>
                      )}
                      {service.code && (
                        <span className="text-xs text-blue-600 font-mono font-medium">{service.code}</span>
                      )}
                      {service.featured && <StarSolidIcon className="h-4 w-4 text-yellow-500" />}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mt-0.5">
                      {service.category_is_vehicle && service.technical_specs?.placa ? (
                        <>
                          <span className="font-bold text-blue-900">{service.technical_specs.placa}</span>
                          {service.technical_specs.alias && (
                            <span className="text-gray-600 ml-1">| {service.technical_specs.alias}</span>
                          )}
                        </>
                      ) : (
                        service.name
                      )}
                    </h3>
                    <p className="text-xs text-gray-500">{service.category_name || 'Sem categoria'}</p>
                    {service.short_description && (
                      <p className="text-xs text-gray-600 truncate">{service.short_description}</p>
                    )}
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(service.availability_status)}`}>
                  {getAvailabilityIcon(service.availability_status)}
                  <span className="ml-1">{service.availability_label || service.availability_status}</span>
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                <div className="flex items-center">
                  <span className="font-medium">Visão Geral:</span>

                  {/* SERVIÇOS: Apenas ícone clicável */}
                  {service.item_type === 'service' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Abrir modal de contratos/clientes para serviços
                        alert('Modal de contratos ativos em desenvolvimento');
                      }}
                      className="text-blue-600 hover:text-blue-800 transition-colors ml-2 inline-flex"
                      title="Ver contratos e clientes"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    /* PRODUTOS: Mostrar disponível/total + ícone para modal */
                    <>
                      <span className="text-green-600 ml-1">{service.available_quantity || 0}</span>
                      <span className="text-gray-400 mx-0.5">/</span>
                      <span className="text-gray-900">{service.total_quantity || 0}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenItemManagement(service, 'all');
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors ml-1 inline-flex"
                        title="Gerenciar itens individuais"
                      >
                        <InformationCircleIcon className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Link to={`/catalog/services/${service.id}`} className="text-blue-600 hover:text-blue-900 p-1" title="Ver">
                  <span className="text-xs">Ver</span>
                </Link>
                {service.category_is_vehicle && (
                  <button
                    onClick={() => handleDuplicate(service.id)}
                    className="text-green-600 hover:text-green-800 p-1"
                    title="Duplicar veículo"
                  >
                    <span className="text-xs">Duplicar</span>
                  </button>
                )}
                <Link to={`/catalog/services/${service.id}/edit`} className="text-gray-600 hover:text-gray-900 p-1" title="Editar">
                  <span className="text-xs">Editar</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {/* Table for Desktop */}
        <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipamento</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Depósito</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visão Geral</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {services.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-25">
                    <td className="px-2 py-1.5">
                      <div className="flex items-center">
                        {service.primary_image?.image_url ? (
                          <img className="h-8 w-8 rounded-lg object-cover mr-2" src={service.primary_image.image_url} alt={service.name} />
                        ) : (
                          <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center mr-2">
                            <span className="text-gray-400 text-xs">IMG</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            {service.category_is_vehicle && (
                              <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                🚗
                              </span>
                            )}
                            {service.code && (
                              <span className="text-xs text-blue-600 font-mono font-medium">{service.code}</span>
                            )}
                            {service.featured && <StarSolidIcon className="h-3 w-3 text-yellow-500" />}
                          </div>
                          <div className="flex flex-col mt-0.5">
                            <span className="text-xs font-medium text-gray-900 truncate max-w-48">
                              {service.category_is_vehicle && service.technical_specs?.placa ? (
                                <>
                                  <span className="font-bold text-blue-900">{service.technical_specs.placa}</span>
                                  {service.technical_specs.alias && (
                                    <span className="text-gray-600 ml-1">| {service.technical_specs.alias}</span>
                                  )}
                                </>
                              ) : (
                                service.name
                              )}
                            </span>
                            {service.short_description && (
                              <div className="text-xs text-gray-500 truncate max-w-48">{service.short_description}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900 truncate max-w-24">{service.category_name || 'Sem categoria'}</td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">
                      {service.primary_warehouse ? (
                        <span className="font-medium">{service.primary_warehouse.code}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {/* SERVIÇOS: Apenas ícone clicável */}
                      {service.item_type === 'service' ? (
                        <div className="flex justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Abrir modal de contratos/clientes para serviços
                              alert('Modal de contratos ativos em desenvolvimento');
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Ver contratos e clientes"
                          >
                            <InformationCircleIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        /* PRODUTOS: Mostrar disponível/total + link para modal de itens */
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-900 font-medium">
                              <span className="text-green-600">{service.available_quantity || 0}</span>
                              <span className="text-gray-400 mx-0.5">/</span>
                              <span>{service.total_quantity || 0}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenItemManagement(service, 'all');
                              }}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Gerenciar itens individuais"
                            >
                              <InformationCircleIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {service.track_individual_items && service.total_quantity > 0 && (
                            <div className="mt-1 w-20 bg-gray-200 rounded-full h-1">
                              <div
                                className="bg-green-600 h-1 rounded-full"
                                style={{
                                  width: `${((service.available_quantity || 0) / service.total_quantity) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex justify-end space-x-1">
                        <Link to={`/catalog/services/${service.id}`} className="text-blue-600 hover:text-blue-900 text-xs p-0.5" title="Ver">
                          Ver
                        </Link>
                        {service.category_is_vehicle && (
                          <button
                            onClick={() => handleDuplicate(service.id)}
                            className="text-green-600 hover:text-green-800 text-xs p-0.5"
                            title="Duplicar veículo"
                          >
                            Duplicar
                          </button>
                        )}
                        <Link to={`/catalog/services/${service.id}/edit`} className="text-gray-600 hover:text-gray-900 text-xs p-0.5" title="Editar">
                          Editar
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Estatísticas no rodapé */}
          <div className="bg-gray-50 px-3 py-2 border-t">
            <div className="flex justify-between items-center text-xs text-gray-500">
              <div className="flex items-center space-x-2">
                <span>{pagination.count} equipamentos encontrados</span>
                <div className="flex items-center space-x-2">
                  <label className="text-xs text-gray-700">Por página:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(parseInt(e.target.value))}
                    className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="flex space-x-2">
                <span>{services.filter(s => s.availability_status === 'available').length} disponíveis</span>
                <span>{services.filter(s => s.featured).length} em destaque</span>
              </div>
            </div>
          </div>
        </div>
        </>
        ) : renderGridView()
      ) : (
        <div className="text-center py-12">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum equipamento encontrado</h3>
          <p className="mt-1 text-xs text-gray-500">
            {searchTerm || selectedCategory || selectedAvailability
              ? 'Tente ajustar os filtros para encontrar equipamentos.'
              : 'Comece criando seu primeiro equipamento.'}
          </p>
          <div className="mt-6">
            <Link
              to="/catalog/services/new"
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Novo Equipamento
            </Link>
          </div>
        </div>
      )}

      {/* Modal de Gerenciamento de Itens */}
      {selectedServiceForItems && (
        <ServiceItemManagement
          isOpen={showItemManagement}
          onClose={handleCloseItemManagement}
          service={selectedServiceForItems}
          initialFilter={initialFilter}
        />
      )}
    </div>
  );
};

export default CatalogList;